import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getUrlParam, setWebTitle, updateUrlParams } from '@/lib/format-utils';
import DatePickerCustom from '@/components/custom-date-picker';
import { useReservations, useServiceBookings, useSavedReports, useDeleteSavedReport, useUpdateSavedReport } from '@/hooks/api-hooks';
import { SkeletonList } from '@/components/snippets';
import BookingsExplorerSummary from '@/components/bookings-explorer-summary';
import ReservationsList from '@/components/reservations-list';
import ServiceBookingsList from '@/components/service-bookings-list';
import SearchInput from '@/components/search-input';
import { Select, MultiSelect, Switch, Button, SegmentedControl } from '@mantine/core';
import { RESERVATION_STATUS_ALL, RESERVATION_STATUS } from '@turizi/shared';
import { filterByMultiWordSearch } from '@/utils/search-utils';
import { ServiceBooking, Reservation, Business, SavedReport } from '@turizi/shared';
import { IconPrinter, IconCalendarEvent, IconEye, IconFilter } from '@tabler/icons-react';
import SavePresetModal from '@/components/save-preset-modal';
import ReservationExportTemplateController from '@/components/reservation-export-template-controller';
import CardModal from '@/components/card-modal';
import ServiceOrderWidget from '@/components/service-order-widget';
import { ServiceTitleStatic } from '@/components/services/service-title';

const DEFAULT_OPTIONS = {
    showServiceTitle: true,
    showServiceOrder: true,
    showReservation: true,
    showBookingId: true,
    showClientName: true,
    showPaxs: true,
    showPaxName: true,
    showPaxPhone: false,
    showPaxEmail: false,
    showProvider: false,
    showCoordinator: false,
    showHour: true,
    showPrice: true,
    showCost: false,
    showPaymentStatus: true,
    slim: true,
    hideServicePackage: true,
    showByCategory: true,
    showByService: false,
    showByProduct: false,
    groupByReservation: true,
    groupByPaxs: false,
    compressIdentical: true,
    selectedAttrKeys: [] as string[],
    hideServiceTitle: false,
    showExternalCode: true
};

// Search query matcher string builder
const getBookingSearchString = (booking: ServiceBooking): string => {
    const parts = [
        booking.id?.toString() || '',
        booking.reservation_id?.toString() || '',
        ServiceTitleStatic(booking.service_data) || '',
        booking.reservation?.client_business?.name || '',
        booking.observations || '',
    ];
    booking.pax_groups?.forEach(pg => {
        const group = pg.pax_group;
        if (group) {
            if (group.name) parts.push(group.name);
            if (group.contact_phone) parts.push(group.contact_phone);
            if (group.coordinator?.name) parts.push(group.coordinator.name);
            if (group.coordinator?.phone) parts.push(group.coordinator.phone);
            group.persons?.forEach(p => {
                parts.push(p.name || '');
                parts.push(p.document_number || '');
                if (p.phone) parts.push(p.phone);
            });
        }
    });
    booking.reservation?.pax_groups?.forEach(group => {
        if (group.name) parts.push(group.name);
        group.persons?.forEach(p => {
            parts.push(p.name || '');
            parts.push(p.document_number || '');
        });
    });
    booking.service_data?.providers?.forEach(prov => {
        parts.push(prov.name || '');
    });
    const orderBookings = booking.service_order_bookings || [];
    orderBookings.forEach(order => {
        parts.push(order.id.toString());
    });
    return parts.join(' ').toLowerCase();
};

export default function ReservationsExportPage(): JSX.Element {
    const { t } = useTranslation();

    setWebTitle(t('reservations_export'));

    const [selected, setSelected] = useState<number[]>([]);

    // Date Range Filters (sync with URL params)
    const [reservationDate, setReservationDate] = useState<string | null>(getUrlParam('reservation_date', null));
    const [startDateRange, setStartDateRange] = useState<[string | null, string | null]>([
        getUrlParam('reservation_start_from'),
        getUrlParam('reservation_start_to')
    ]);
    const [endDateRange, setEndDateRange] = useState<[string | null, string | null]>([
        getUrlParam('reservation_end_from'),
        getUrlParam('reservation_end_to')
    ]);
    const [bookingDate, setBookingDate] = useState<string | null>(getUrlParam('booking_date', null));

    const [searchMode, setSearchMode] = useState<'reservation' | 'booking'>(() => {
        return getUrlParam('booking_date') ? 'booking' : 'reservation';
    });

    // Query Filters (sync with URL params)
    const [searchText, setSearchText] = useState<string>(getUrlParam('search', ''));
    const [selectedCategories, setSelectedCategories] = useState<string[]>(() => {
        const val = getUrlParam('category', null);
        return val ? val.split(',').filter(Boolean) : [];
    });
    const [selectedProviders, setSelectedProviders] = useState<string[]>(() => {
        const val = getUrlParam('provider', null);
        return val ? val.split(',').filter(Boolean) : [];
    });
    const [selectedCoordinators, setSelectedCoordinators] = useState<string[]>(() => {
        const val = getUrlParam('coordinator', null);
        return val ? val.split(',').filter(Boolean) : [];
    });
    const [selectedClients, setSelectedClients] = useState<string[]>(() => {
        const val = getUrlParam('client', null);
        return val ? val.split(',').filter(Boolean) : [];
    });
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>(() => {
        const val = getUrlParam('status', null);
        return val ? val.split(',').filter(Boolean) : [];
    });
    const [selectedPaymentStatuses, setSelectedPaymentStatuses] = useState<string[]>(() => {
        const val = getUrlParam('payment_status', null);
        return val ? val.split(',').filter(Boolean) : [];
    });
    const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>(() => {
        const val = getUrlParam('service_id', null);
        return val ? val.split(',').filter(Boolean) : [];
    });
    const [selectedHierarchyFilter, setSelectedHierarchyFilter] = useState<'all' | 'parents_only' | 'children_only'>('all');
    const [selectedBookingAttrs, setSelectedBookingAttrs] = useState<Record<string, string[]>>({});
    const [selectedServiceAttrs, setSelectedServiceAttrs] = useState<Record<string, string[]>>({});

    const [editable, setEditable] = useState<boolean>(false);

    // Visual Display Options (sync with localStorage)
    const [displayOptions, setDisplayOptions] = useState(() => {
        try {
            const saved = localStorage.getItem('bookings_explorer_display_options');
            return saved ? { ...DEFAULT_OPTIONS, ...JSON.parse(saved) } : DEFAULT_OPTIONS;
        } catch {
            return DEFAULT_OPTIONS;
        }
    });

    // Save display options to localStorage
    useEffect(() => {
        localStorage.setItem('bookings_explorer_display_options', JSON.stringify(displayOptions));
    }, [displayOptions]);

    // Saved Reports (Filter Presets)
    const { data: savedReportsRes } = useSavedReports();
    const savedReports = savedReportsRes?.data || [];
    const deletePreset = useDeleteSavedReport();
    const updatePreset = useUpdateSavedReport();

    const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
    const [showSaveModal, setShowSaveModal] = useState(false);

    // Toggled Modals for Filters & Customization
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [showDisplayModal, setShowDisplayModal] = useState(false);

    const activeFiltersCount = useMemo(() => {
        let count = 0;
        if (selectedCategories.length > 0) count += selectedCategories.length;
        if (selectedClients.length > 0) count += selectedClients.length;
        if (selectedProviders.length > 0) count += selectedProviders.length;
        if (selectedCoordinators.length > 0) count += selectedCoordinators.length;
        if (selectedStatuses.length > 0) count += selectedStatuses.length;
        if (selectedPaymentStatuses.length > 0) count += selectedPaymentStatuses.length;
        if (selectedServiceIds.length > 0) count += selectedServiceIds.length;

        Object.values(selectedBookingAttrs).forEach(arr => {
            if (arr && arr.length > 0) count += arr.length;
        });
        Object.values(selectedServiceAttrs).forEach(arr => {
            if (arr && arr.length > 0) count += arr.length;
        });

        return count;
    }, [
        selectedCategories,
        selectedClients,
        selectedProviders,
        selectedCoordinators,
        selectedStatuses,
        selectedPaymentStatuses,
        selectedServiceIds,
        selectedBookingAttrs,
        selectedServiceAttrs
    ]);

    const activeColumnsCount = useMemo(() => {
        let count = 0;
        if (displayOptions.showServiceTitle && !displayOptions.hideServiceTitle) count++;
        if (displayOptions.showServiceOrder) count++;
        if (displayOptions.showReservation) count++;
        if (displayOptions.showBookingId) count++;
        if (displayOptions.showClientName) count++;
        if (displayOptions.showPaxs) {
            count++;
            if (displayOptions.showPaxName) count++;
            if (displayOptions.showPaxPhone) count++;
            if (displayOptions.showPaxEmail) count++;
        }
        if (displayOptions.showProvider) count++;
        if (displayOptions.showCoordinator) count++;
        if (displayOptions.showHour) count++;
        if (displayOptions.showPrice) count++;
        if (displayOptions.showCost) count++;
        if (displayOptions.showPaymentStatus) count++;

        if (displayOptions.selectedAttrKeys) {
            count += displayOptions.selectedAttrKeys.length;
        }
        return count;
    }, [displayOptions]);

    const loadPreset = (preset: SavedReport) => {
        const params = preset.params || {};

        setReservationDate(params.reservationDate !== undefined ? params.reservationDate : null);
        setStartDateRange(params.startDateRange !== undefined ? params.startDateRange : [null, null]);
        setEndDateRange(params.endDateRange !== undefined ? params.endDateRange : [null, null]);
        setBookingDate(params.bookingDate !== undefined ? params.bookingDate : null);
        setSearchMode(params.bookingDate ? 'booking' : 'reservation');
        setSearchText(params.searchText !== undefined ? params.searchText : '');
        setSelectedCategories(params.selectedCategories !== undefined ? params.selectedCategories : (params.category ? params.category.split(',').filter(Boolean) : []));
        setSelectedProviders(params.selectedProviders !== undefined ? params.selectedProviders : (params.selectedProvider ? [params.selectedProvider] : (params.provider ? params.provider.split(',').filter(Boolean) : [])));
        setSelectedCoordinators(params.selectedCoordinators !== undefined ? params.selectedCoordinators : (params.coordinator ? params.coordinator.split(',').filter(Boolean) : []));
        setSelectedClients(params.selectedClients !== undefined ? params.selectedClients : (params.selectedClient ? [params.selectedClient] : (params.client ? params.client.split(',').filter(Boolean) : [])));
        setSelectedStatuses(params.selectedStatuses !== undefined ? params.selectedStatuses : (params.selectedStatus ? [params.selectedStatus] : (params.status ? params.status.split(',').filter(Boolean) : [])));
        setSelectedPaymentStatuses(params.selectedPaymentStatuses !== undefined ? params.selectedPaymentStatuses : (params.selectedPaymentStatus ? [params.selectedPaymentStatus] : (params.payment_status ? params.payment_status.split(',').filter(Boolean) : [])));
        setSelectedServiceIds(params.selectedServiceIds !== undefined ? params.selectedServiceIds : (params.service_id ? params.service_id.split(',').filter(Boolean) : []));
        setSelectedBookingAttrs(params.selectedBookingAttrs !== undefined ? params.selectedBookingAttrs : {});
        setSelectedServiceAttrs(params.selectedServiceAttrs !== undefined ? params.selectedServiceAttrs : {});

        setSelectedPresetId(preset.id.toString());
    };

    const handleDeletePreset = async (id: string) => {
        if (window.confirm(t('confirm_delete'))) {
            try {
                await deletePreset.mutateAsync(id);
                if (selectedPresetId === id) {
                    setSelectedPresetId(null);
                }
            } catch (err) {
                console.error('Error deleting preset:', err);
            }
        }
    };

    const handleUpdatePreset = async (presetId: string) => {
        const preset = savedReports.find(r => r.id.toString() === presetId);
        if (!preset) return;

        try {
            await updatePreset.mutateAsync({
                ...preset,
                params: {
                    reservationDate,
                    startDateRange,
                    endDateRange,
                    bookingDate,
                    searchText,
                    selectedCategories,
                    selectedProviders,
                    selectedCoordinators,
                    selectedClients,
                    selectedStatuses,
                    selectedPaymentStatuses,
                    selectedServiceIds,
                    selectedBookingAttrs,
                    selectedServiceAttrs,
                }
            });
        } catch (err) {
            console.error('Error updating preset:', err);
        }
    };

    // Fetch Reservations (Service Bookings are loaded within reservations)
    const {
        data: reservationsData,
        isLoading: isLoadingReservations
    } = useReservations({
        reservation_start_from: startDateRange[0],
        reservation_start_to: startDateRange[1],
        reservation_end_from: endDateRange[0],
        reservation_end_to: endDateRange[1],
        reservation_date: reservationDate,
    }, {
        enabled: !bookingDate && ((!!startDateRange[0] && !!startDateRange[1]) || (!!endDateRange[0] && !!endDateRange[1]) || !!reservationDate)
    });

    const reservations = reservationsData?.data || [];

    // Fetch Service Bookings directly by Booking Date
    const {
        data: bookingsData,
        isLoading: isLoadingBookings
    } = useServiceBookings({
        booking_date: bookingDate,
    }, {
        enabled: !!bookingDate
    });

    const isLoading = bookingDate ? isLoadingBookings : isLoadingReservations;

    // Sync Filter & Sorting parameters to the URL
    useEffect(() => {
        updateUrlParams({
            reservation_start_from: startDateRange[0],
            reservation_start_to: startDateRange[1],
            reservation_end_from: endDateRange[0],
            reservation_end_to: endDateRange[1],
            reservation_date: reservationDate,
            booking_date: bookingDate,
            search: searchText || null,
            category: selectedCategories.length > 0 ? selectedCategories.join(',') : null,
            provider: selectedProviders.length > 0 ? selectedProviders.join(',') : null,
            coordinator: selectedCoordinators.length > 0 ? selectedCoordinators.join(',') : null,
            client: selectedClients.length > 0 ? selectedClients.join(',') : null,
            status: selectedStatuses.length > 0 ? selectedStatuses.join(',') : null,
            payment_status: selectedPaymentStatuses.length > 0 ? selectedPaymentStatuses.join(',') : null,
            service_id: selectedServiceIds.length > 0 ? selectedServiceIds.join(',') : null,
        });
    }, [startDateRange, endDateRange, reservationDate, bookingDate, searchText, selectedCategories, selectedProviders, selectedCoordinators, selectedClients, selectedStatuses, selectedPaymentStatuses, selectedServiceIds]);

    // Flatten and parse service bookings from the loaded reservations or directly use loaded bookings
    const allBookings = useMemo(() => {
        if (bookingDate) {
            return bookingsData?.data || [];
        }

        const bookingsList: ServiceBooking[] = [];
        reservations.forEach((res) => {
            if (res.service_bookings) {
                res.service_bookings.forEach((booking) => {
                    bookingsList.push({
                        ...booking,
                        reservation: res // Back-reference for easy access in row renderers
                    });
                });
            }
        });
        return bookingsList;
    }, [bookingDate, bookingsData, reservations]);

    // Dynamic Filter lists from current bookings
    const categoryOptions = useMemo(() => {
        const cats = new Set<string>();
        allBookings.forEach((b) => {
            if (b.service_data?.category) cats.add(b.service_data.category);
        });
        return Array.from(cats).sort().map(cat => ({ value: cat, label: t(cat) }));
    }, [allBookings, t]);

    const providerOptions = useMemo(() => {
        const provMap = new Map<number, string>();
        allBookings.forEach((b) => {
            b.service_data?.providers?.forEach((p: Business) => {
                provMap.set(p.id, p.name);
            });
        });
        return Array.from(provMap.entries()).map(([id, name]) => ({
            value: id.toString(),
            label: `${name} (#${id})`
        })).sort((a, b) => a.label.localeCompare(b.label));
    }, [allBookings]);

    const coordinatorOptions = useMemo(() => {
        const coordMap = new Map<number, string>();
        allBookings.forEach((b) => {
            if (b.coordinator) {
                coordMap.set(b.coordinator.id, b.coordinator.name);
            }
        });
        return Array.from(coordMap.entries()).map(([id, name]) => ({
            value: id.toString(),
            label: `${name} (#${id})`
        })).sort((a, b) => a.label.localeCompare(b.label));
    }, [allBookings]);

    const clientOptions = useMemo(() => {
        const clientMap = new Map<string, string>();
        allBookings.forEach((b) => {
            const res = b.reservation;
            if (res) {
                if (res.client_business) {
                    clientMap.set(`business:${res.client_business.id}`, res.client_business.name);
                }
            }
        });
        return Array.from(clientMap.entries()).map(([key, name]) => ({
            value: key,
            label: `${name} (#${key.split(':')[1]})`
        })).sort((a, b) => a.label.localeCompare(b.label));
    }, [allBookings]);

    const paymentStatusOptions = useMemo(() => {
        const statuses = new Set<string>();
        allBookings.forEach((b) => {
            const status = b.reservation?.payment_status;
            if (status) statuses.add(status);
        });
        return Array.from(statuses).sort().map(status => ({ value: status, label: t(status) }));
    }, [allBookings, t]);

    const serviceOptions = useMemo(() => {
        const serviceMap = new Map<number, string>();
        allBookings.forEach((b) => {
            serviceMap.set(b.service_id, ServiceTitleStatic(b.service_data));
        });
        return Array.from(serviceMap.entries()).map(([id, name]) => ({
            value: id.toString(),
            label: `${name} (#${id})`
        })).sort((a, b) => a.label.localeCompare(b.label));
    }, [allBookings]);

    // Extract unique attribute keys across all loaded bookings
    const allAttrKeys = useMemo(() => {
        const keys = new Set<string>();
        allBookings.forEach((b) => {
            b.self_attrs?.forEach((attr) => {
                if (attr.key) {
                    keys.add(attr.key);
                }
            });
        });
        return Array.from(keys).sort();
    }, [allBookings]);

    // Filter bookings by non-custom-attribute filters first to use as a base for custom attribute options
    const bookingsFilteredByNonAttributeFilters = useMemo(() => {
        let filtered = allBookings;

        // Category filter
        if (selectedCategories.length > 0) {
            filtered = filtered.filter(b => b.service_data?.category && selectedCategories.includes(b.service_data.category));
        }

        // Provider filter
        if (selectedProviders.length > 0) {
            filtered = filtered.filter(b => b.selected_provider_id && selectedProviders.map(Number).includes(b.selected_provider_id));
        }

        // Coordinator filter
        if (selectedCoordinators.length > 0) {
            filtered = filtered.filter(b => b.coordinator_id && selectedCoordinators.map(Number).includes(b.coordinator_id));
        }

        // Client filter
        if (selectedClients.length > 0) {
            filtered = filtered.filter(b => {
                const res = b.reservation;
                if (!res) return false;
                return selectedClients.some(clientStr => {
                    const [clientType, clientId] = clientStr.split(':');
                    if (clientType === 'business') {
                        return res.client_business?.id === Number(clientId);
                    }
                    return false;
                });
            });
        }

        // Reservation Status filter
        if (selectedStatuses.length > 0) {
            filtered = filtered.filter(b => b.reservation?.status && selectedStatuses.includes(b.reservation.status));
        }

        // Payment Status filter
        if (selectedPaymentStatuses.length > 0) {
            filtered = filtered.filter(b => b.reservation?.payment_status && selectedPaymentStatuses.includes(b.reservation.payment_status));
        }

        // Service ID filter
        if (selectedServiceIds.length > 0) {
            filtered = filtered.filter(b => b.service_id && selectedServiceIds.map(Number).includes(b.service_id));
        }

        // Reservation Hierarchy filter (all / parents_only / children_only)
        if (selectedHierarchyFilter === 'parents_only') {
            filtered = filtered.filter(b => !b.reservation?.parent_id && !b.reservation?.parent);
        } else if (selectedHierarchyFilter === 'children_only') {
            filtered = filtered.filter(b => Boolean(b.reservation?.parent_id || b.reservation?.parent));
        }

        // Search text query filter
        if (searchText) {
            filtered = filterByMultiWordSearch(
                filtered,
                searchText,
                (b: ServiceBooking) => getBookingSearchString(b)
            );
        }

        return filtered;
    }, [allBookings, selectedCategories, selectedProviders, selectedCoordinators, selectedClients, selectedStatuses, selectedPaymentStatuses, selectedServiceIds, selectedHierarchyFilter, searchText]);

    // Extract dynamic booking level custom attribute options (from b.self_attrs)
    const bookingAttributeFilters = useMemo(() => {
        const result = new Map<string, string[]>();

        // 1. Get all unique keys and track their sort numbers from bookingsFilteredByNonAttributeFilters
        const keys = new Set<string>();
        const keySortValues = new Map<string, number>();

        bookingsFilteredByNonAttributeFilters.forEach((b) => {
            b.self_attrs?.forEach((attr) => {
                if (attr.key) {
                    keys.add(attr.key);
                    if (attr.sort !== undefined && attr.sort !== null) {
                        const sVal = Number(attr.sort);
                        if (!isNaN(sVal)) {
                            if (!keySortValues.has(attr.key) || sVal < keySortValues.get(attr.key)!) {
                                keySortValues.set(attr.key, sVal);
                            }
                        }
                    }
                }
            });
        });

        const sortedKeys = Array.from(keys).sort((a, b) => {
            const sortA = keySortValues.has(a) ? keySortValues.get(a)! : 999;
            const sortB = keySortValues.has(b) ? keySortValues.get(b)! : 999;
            if (sortA !== sortB) {
                return sortA - sortB;
            }
            return a.localeCompare(b);
        });

        // 2. For each key, compute the available values by filtering bookings with all OTHER key filters
        sortedKeys.forEach((key) => {
            const values = new Set<string>();

            // Filter bookings by other booking attribute filters and all service attribute filters
            let bookingsForKey = bookingsFilteredByNonAttributeFilters;
            Object.entries(selectedBookingAttrs).forEach(([otherKey, selectedValues]) => {
                if (otherKey !== key && selectedValues && selectedValues.length > 0) {
                    bookingsForKey = bookingsForKey.filter((b) => {
                        const bAttr = b.self_attrs?.find(attr => attr.key === otherKey);
                        const bVal = bAttr && bAttr.value !== undefined && bAttr.value !== null && bAttr.value !== '' ? String(bAttr.value).trim() : '';
                        return bVal && selectedValues.includes(bVal);
                    });
                }
            });

            Object.entries(selectedServiceAttrs).forEach(([sKey, selectedValues]) => {
                if (selectedValues && selectedValues.length > 0) {
                    bookingsForKey = bookingsForKey.filter((b) => {
                        const sAttr = b.service_data?.self_attrs?.find(attr => attr.key === sKey);
                        const sVal = sAttr && sAttr.value !== undefined && sAttr.value !== null && sAttr.value !== '' ? String(sAttr.value).trim() : '';
                        return sVal && selectedValues.includes(sVal);
                    });
                }
            });

            // Extract values for this key
            bookingsForKey.forEach((b) => {
                b.self_attrs?.forEach((attr) => {
                    if (attr.key === key && attr.value !== undefined && attr.value !== null && attr.value !== '') {
                        const valStr = String(attr.value).trim();
                        if (valStr) values.add(valStr);
                    }
                });
            });

            if (values.size > 0) {
                result.set(key, Array.from(values).sort());
            }
        });

        return result;
    }, [bookingsFilteredByNonAttributeFilters, selectedBookingAttrs, selectedServiceAttrs]);

    // Extract dynamic service level custom attribute options (from b.service_data.self_attrs)
    const serviceAttributeFilters = useMemo(() => {
        const result = new Map<string, string[]>();

        // 1. Get all unique keys and track their sort numbers from bookingsFilteredByNonAttributeFilters
        const keys = new Set<string>();
        const keySortValues = new Map<string, number>();

        bookingsFilteredByNonAttributeFilters.forEach((b) => {
            b.service_data?.self_attrs?.forEach((attr) => {
                if (attr.key) {
                    keys.add(attr.key);
                    if (attr.sort !== undefined && attr.sort !== null) {
                        const sVal = Number(attr.sort);
                        if (!isNaN(sVal)) {
                            if (!keySortValues.has(attr.key) || sVal < keySortValues.get(attr.key)!) {
                                keySortValues.set(attr.key, sVal);
                            }
                        }
                    }
                }
            });
        });

        const sortedKeys = Array.from(keys).sort((a, b) => {
            const sortA = keySortValues.has(a) ? keySortValues.get(a)! : 999;
            const sortB = keySortValues.has(b) ? keySortValues.get(b)! : 999;
            if (sortA !== sortB) {
                return sortA - sortB;
            }
            return a.localeCompare(b);
        });

        // 2. For each key, compute the available values by filtering bookings with all OTHER key filters
        sortedKeys.forEach((key) => {
            const values = new Set<string>();

            // Filter bookings by all booking attribute filters and other service attribute filters
            let bookingsForKey = bookingsFilteredByNonAttributeFilters;
            Object.entries(selectedBookingAttrs).forEach(([bKey, selectedValues]) => {
                if (selectedValues && selectedValues.length > 0) {
                    bookingsForKey = bookingsForKey.filter((b) => {
                        const bAttr = b.self_attrs?.find(attr => attr.key === bKey);
                        const bVal = bAttr && bAttr.value !== undefined && bAttr.value !== null && bAttr.value !== '' ? String(bAttr.value).trim() : '';
                        return bVal && selectedValues.includes(bVal);
                    });
                }
            });

            Object.entries(selectedServiceAttrs).forEach(([otherKey, selectedValues]) => {
                if (otherKey !== key && selectedValues && selectedValues.length > 0) {
                    bookingsForKey = bookingsForKey.filter((b) => {
                        const sAttr = b.service_data?.self_attrs?.find(attr => attr.key === otherKey);
                        const sVal = sAttr && sAttr.value !== undefined && sAttr.value !== null && sAttr.value !== '' ? String(sAttr.value).trim() : '';
                        return sVal && selectedValues.includes(sVal);
                    });
                }
            });

            // Extract values for this key
            bookingsForKey.forEach((b) => {
                b.service_data?.self_attrs?.forEach((attr) => {
                    if (attr.key === key && attr.value !== undefined && attr.value !== null && attr.value !== '') {
                        const valStr = String(attr.value).trim();
                        if (valStr) values.add(valStr);
                    }
                });
            });

            if (values.size > 0) {
                result.set(key, Array.from(values).sort());
            }
        });

        return result;
    }, [bookingsFilteredByNonAttributeFilters, selectedBookingAttrs, selectedServiceAttrs]);

    // Apply Filter & Sort logics
    const processedBookings = useMemo(() => {
        let filtered = bookingsFilteredByNonAttributeFilters;

        // Booking level custom attribute filters
        Object.entries(selectedBookingAttrs).forEach(([key, selectedValues]) => {
            if (selectedValues && selectedValues.length > 0 && bookingAttributeFilters.has(key)) {
                filtered = filtered.filter((b) => {
                    const bAttr = b.self_attrs?.find(attr => attr.key === key);
                    const bVal = bAttr && bAttr.value !== undefined && bAttr.value !== null && bAttr.value !== '' ? String(bAttr.value).trim() : '';
                    return bVal && selectedValues.includes(bVal);
                });
            }
        });

        // Service level custom attribute filters
        Object.entries(selectedServiceAttrs).forEach(([key, selectedValues]) => {
            if (selectedValues && selectedValues.length > 0 && serviceAttributeFilters.has(key)) {
                filtered = filtered.filter((b) => {
                    const sAttr = b.service_data?.self_attrs?.find(attr => attr.key === key);
                    const sVal = sAttr && sAttr.value !== undefined && sAttr.value !== null && sAttr.value !== '' ? String(sAttr.value).trim() : '';
                    return sVal && selectedValues.includes(sVal);
                });
            }
        });
        return filtered;
    }, [bookingsFilteredByNonAttributeFilters, selectedBookingAttrs, selectedServiceAttrs, bookingAttributeFilters, serviceAttributeFilters]);

    // Group processed bookings by reservation if enabled
    const bookingsGroupedByReservation = useMemo(() => {
        const resMap = new Map<number, { reservation: Reservation, bookings: ServiceBooking[] }>();
        processedBookings.forEach((b) => {
            const resId = b.reservation_id;
            if (!resId) return;
            const res = b.reservation;
            if (!res) return;
            if (!resMap.has(resId)) {
                resMap.set(resId, { reservation: res, bookings: [] });
            }
            resMap.get(resId)!.bookings.push(b);
        });
        return Array.from(resMap.values());
    }, [processedBookings]);

    const reservationsToRender = useMemo(() => {
        return bookingsGroupedByReservation.map(({ reservation, bookings }) => ({
            ...reservation,
            service_bookings: bookings,
        }));
    }, [bookingsGroupedByReservation]);


    const handleToggleOption = (key: keyof typeof DEFAULT_OPTIONS) => {
        setDisplayOptions((prev: any) => {
            return { ...prev, [key]: !prev[key] };
        });
    };

    return (
        <div>
            <style dangerouslySetInnerHTML={{
                __html: `
                @media print {
                    .no-print {
                        display: none !important;
                    }
                    /* Reset default mantine layout or container spacing on print */
                    .mantine-AppShell-main {
                        padding: 0 !important;
                        margin: 0 !important;
                        background: white !important;
                    }
                    body, html, #root {
                        background: white !important;
                    }
                }
            `}} />

            <div className="panel-content space-y-2">

                {/* 1. Reorganized Date Selector & Preset Section */}
                <div className="no-print flex justify-between items-stretch gap-4">
                    {/* Search Mode & Date Selectors Card */}
                    <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4 flex flex-col justify-between">
                        {/* SegmentedControl at the top */}
                        <div className="flex gap-3 items-center justify-start mb-3 pb-2 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                                <IconCalendarEvent size={18} className="text-indigo-500" />
                                <span className="font-semibold text-xs text-slate-700 uppercase tracking-wider">
                                    {t('search_by')}
                                </span>
                            </div>
                            <SegmentedControl
                                value={searchMode}
                                onChange={(value: 'reservation' | 'booking') => {
                                    setSearchMode(value);
                                    // Automatically clear opposite fields to avoid query conflicts
                                    if (value === 'reservation') {
                                        setBookingDate(null);
                                    } else {
                                        setReservationDate(null);
                                        setStartDateRange([null, null]);
                                        setEndDateRange([null, null]);
                                    }
                                }}
                                data={[
                                    { label: t('reservation'), value: 'reservation' },
                                    { label: t('Booking'), value: 'booking' }
                                ]}
                                size="md"
                                color="black"
                                radius="xl"
                            />
                        </div>
                        {/* Conditional Rendering of Inputs */}
                        {searchMode === 'reservation' ? (
                            <div className="flex items-end gap-8">

                                <div className="flex gap-2">
                                    <DatePickerCustom
                                        value={startDateRange}
                                        onChange={(val) => {
                                            setStartDateRange(val);
                                            if (val[0] || val[1]) {
                                                setReservationDate(null);
                                                setBookingDate(null);
                                                setEndDateRange([null, null]);
                                            }
                                        }}
                                        clearable={true}
                                        label={t('starts_between')}
                                        className="font-bold"
                                        valueFormat="DD MMM YYYY"
                                    />
                                    <DatePickerCustom
                                        value={endDateRange}
                                        onChange={(val) => {
                                            setEndDateRange(val);
                                            if (val[0] || val[1]) {
                                                setReservationDate(null);
                                                setBookingDate(null);
                                                setStartDateRange([null, null]);
                                            }
                                        }}
                                        clearable={true}
                                        label={t('ends_between')}
                                        className="font-bold"
                                        valueFormat="DD MMM YYYY"
                                    />
                                </div>
                                <div className='text-gray-300'>|</div>
                                <DatePickerCustom
                                    value={reservationDate || ''}
                                    onChange={(val) => {
                                        const selected = val[0] || null;
                                        setReservationDate(selected);
                                        if (selected) {
                                            setStartDateRange([null, null]);
                                            setEndDateRange([null, null]);
                                            setBookingDate(null);
                                        }
                                    }}
                                    clearable={true}
                                    label={t('active_during_date')}
                                    className="font-bold"
                                    valueFormat="DD MMM YYYY"
                                />
                            </div>
                        ) : (
                            <DatePickerCustom
                                value={bookingDate || ''}
                                onChange={(val) => {
                                    const selected = val[0] || null;
                                    setBookingDate(selected);
                                    if (selected) {
                                        setReservationDate(null);
                                        setStartDateRange([null, null]);
                                        setEndDateRange([null, null]);
                                    }
                                }}
                                clearable={true}
                                label={t('booking_start_date')}
                                valueFormat='DD MMM YYYY'
                                className="font-bold w-[300px]"
                            />
                        )}
                    </div>
                    {/* Saved Presets Card */}
                    <div className='flex-1 space-y-2'>
                        <ReservationExportTemplateController
                            savedReports={savedReports}
                            selectedPresetId={selectedPresetId}
                            onSelectPreset={(preset) => {
                                if (preset) {
                                    loadPreset(preset);
                                } else {
                                    setSelectedPresetId(null);
                                }
                            }}
                            onDeletePreset={handleDeletePreset}
                            onSaveClick={() => setShowSaveModal(true)}
                            onUpdatePreset={() => selectedPresetId && handleUpdatePreset(selectedPresetId)}
                        />
                    </div>
                </div>

                <div className='flex items-center gap-2'>

                    <div className='p-2 bg-slate-50 border border-slate-200 rounded-md flex gap-3 items-center'>
                        <div className='font-bold text-xxs'>
                            {t('group_by')}:
                        </div>
                        {searchMode === 'reservation' && (
                            <>
                                <Switch
                                    checked={displayOptions.groupByPaxs}
                                    label={t('paxs')}
                                    onChange={() => handleToggleOption('groupByPaxs')}
                                    size="xs"
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.compressIdentical !== false}
                                    label={t('compress_identical')}
                                    onChange={() => handleToggleOption('compressIdentical')}
                                    size="xs"
                                    color="indigo"
                                />
                            </>
                        )}
                        {searchMode === 'booking' && (
                            <>
                                <Switch
                                    checked={displayOptions.showByCategory}
                                    label={t('category')}
                                    onChange={() => {
                                        setDisplayOptions((prev: any) => ({
                                            ...prev,
                                            showByCategory: !prev.showByCategory,
                                            showByService: false,
                                            showByProduct: false,
                                        }));
                                    }}
                                    size="xs"
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.showByService}
                                    label={t('service')}
                                    onChange={() => {
                                        setDisplayOptions((prev: any) => ({
                                            ...prev,
                                            showByService: !prev.showByService,
                                            showByCategory: false,
                                            showByProduct: false,
                                        }));
                                    }}
                                    size="xs"
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.showByProduct}
                                    label={t('product')}
                                    onChange={() => {
                                        setDisplayOptions((prev: any) => ({
                                            ...prev,
                                            showByProduct: !prev.showByProduct,
                                            showByCategory: false,
                                            showByService: false,
                                        }));
                                    }}
                                    size="xs"
                                    color="indigo"
                                />
                            </>
                        )}
                    </div>
                    {/* filters and columns */}
                    <div className="flex items-center gap-2">
                        <Button
                            variant="subtle"
                            color="black"
                            onClick={() => setShowFiltersModal(true)}
                            leftSection={<IconFilter size={16} />}
                            size="sm"
                        >
                            {t('filters')}
                            {activeFiltersCount > 0 && (
                                <div className="text-xxs font-bold text-red-700 rounded-md p-1 bg-white border border-red-200 ml-1">
                                    {activeFiltersCount}
                                </div>
                            )}
                        </Button>
                        <Button
                            variant="subtle"
                            color="black"
                            onClick={() => setShowDisplayModal(true)}
                            leftSection={<IconEye size={16} />}
                            size="sm"
                        >
                            {t('columns')}
                            {activeColumnsCount > 0 && (
                                <div className="text-xxs font-bold text-red-700 rounded-md p-1 bg-white border border-red-200 ml-1">
                                    {activeColumnsCount}
                                </div>
                            )}
                        </Button>
                    </div>
                    <SearchInput
                        initialValue={searchText}
                        onSearch={(val) => setSearchText(val)}
                        size="sm"
                        placeholder={t('search_placeholder')}
                        className="w-full sm:w-[200px]"
                    />
                    {/* Control Toolbar */}
                    <div className="flex items-center justify-between gap-3 ">
                        {/* Share, Print and Export */}
                        <Button
                            onClick={() => window.print()}
                            variant="light"
                            color="gray"
                            radius="xl"
                            size='xs'
                            leftSection={<IconPrinter size={16} />}
                        >
                            {t('print')}
                        </Button>
                        <div className={`
                                whitespace-nowrap flex items-center gap-2 p-1.5 rounded-lg border
                                ${editable ? 'bg-red-50 border-red-500' : 'bg-slate-50 border-slate-200'}
                                `}>
                            <Switch
                                checked={editable}
                                onChange={(event) => setEditable(event.currentTarget.checked)}
                                label={t('allow_edit')}
                                color="green"
                                size="xs"
                            />
                        </div>
                    </div>
                </div>

                {/* 6. List representation */}
                <div className="print-container">
                    {searchMode === 'reservation' ? (
                        <ReservationsList
                            showFilters={false}
                            onSelectRow={(id) => {
                                setSelected((prev) => {
                                    if (prev.includes(id)) {
                                        return prev.filter((item) => item !== id);
                                    }
                                    return [...prev, id];
                                });
                            }}
                            selectedIds={selected}
                            reservations={reservationsToRender}
                            displayOptions={{ ...displayOptions, editable }}
                        />
                    ) : (
                        <ServiceBookingsList
                            showHeader={false}
                            onSelectRow={(id) => {
                                setSelected((prev) => {
                                    if (prev.includes(id)) {
                                        return prev.filter((item) => item !== id);
                                    }
                                    return [...prev, id];
                                });
                            }}
                            selectedIds={selected}
                            showBookingId={displayOptions.showBookingId}
                            showByCategory={displayOptions.showByCategory}
                            showByService={displayOptions.showByService}
                            showByProduct={displayOptions.showByProduct}
                            bookings={processedBookings}
                            showServiceOrder={displayOptions.showServiceOrder}
                            showReservation={displayOptions.showReservation}
                            showClientName={displayOptions.showClientName}
                            showPrice={displayOptions.showPrice}
                            showCost={displayOptions.showCost}
                            showPaymentStatus={displayOptions.showPaymentStatus}
                            showPaxs={displayOptions.showPaxs}
                            showPhone={displayOptions.showPaxPhone}
                            showPaxName={displayOptions.showPaxName}
                            showHour={displayOptions.showHour}
                            showProvider={displayOptions.showProvider}
                            showCoordinator={displayOptions.showCoordinator}
                            hideServicePackage={displayOptions.hideServicePackage}
                            slim={displayOptions.slim}
                            editable={editable}
                            selectedAttrKeys={displayOptions.selectedAttrKeys}
                        />
                    )}
                </div>
            </div>

            <ServiceOrderWidget
                selectedBookings={
                    processedBookings.filter(booking => selected.includes(booking.id))
                }
                onClearSelection={() => {
                    setSelected([]);
                }}
            />

            {/* summary here for ALL bookings */}
            {processedBookings.length > 0 && !isLoading && (
                <div className='print:hidden'>
                    <div className='mt-6 mb-2 ml-2 font-bold text-sm'>
                        {t('summary')}
                    </div>
                    <BookingsExplorerSummary
                        bookings={processedBookings}
                        displayOptions={displayOptions}
                    />
                </div>
            )}


            {/* Filters Modal */}
            <CardModal
                size="xl"
                opened={showFiltersModal}
                onClose={() => setShowFiltersModal(false)}
                header={<div className="font-bold text-lg">{t('filters')}</div>}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="subtle"
                            color="gray"
                            onClick={() => {
                                setSelectedCategories([]);
                                setSelectedProviders([]);
                                setSelectedClients([]);
                                setSelectedStatuses([]);
                                setSelectedPaymentStatuses([]);
                                setSelectedServiceIds([]);
                                setSelectedHierarchyFilter('all');
                                setSelectedBookingAttrs({});
                                setSelectedServiceAttrs({});
                            }}
                        >
                            {t('clear_all')}
                        </Button>
                        <Button
                            color="black"
                            onClick={() => setShowFiltersModal(false)}
                        >
                            {t('apply')}
                        </Button>
                    </div>
                }
            >
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Select
                            className='w-[100%]'
                            label={t('reservation_type')}
                            placeholder={t('reservation_type')}
                            value={selectedHierarchyFilter}
                            onChange={(val) => setSelectedHierarchyFilter((val as any) || 'all')}
                            data={[
                                { value: 'all', label: t('all') },
                                { value: 'parents_only', label: t('main_reservations_only') },
                                { value: 'children_only', label: t('child_reservations_only') },
                            ]}
                            radius="md"
                            size="sm"
                        />
                        <MultiSelect
                            className='w-[100%]'
                            clearable
                            searchable
                            label={t('category')}
                            placeholder={t('category')}
                            value={selectedCategories}
                            onChange={(val) => setSelectedCategories(val)}
                            data={categoryOptions}
                            radius="md"
                            size="sm"
                        />
                        <MultiSelect
                            className='w-[100%]'
                            clearable
                            searchable
                            label={t('client')}
                            placeholder={t('client')}
                            value={selectedClients}
                            onChange={(val) => setSelectedClients(val)}
                            data={clientOptions}
                            radius="md"
                            size="sm"
                        />
                        <MultiSelect
                            className='w-[100%]'
                            clearable
                            searchable
                            label={t('provider')}
                            placeholder={t('provider')}
                            value={selectedProviders}
                            onChange={(val) => setSelectedProviders(val)}
                            data={providerOptions}
                            radius="md"
                            size="sm"
                        />
                        <MultiSelect
                            className='w-[100%]'
                            clearable
                            searchable
                            label={t('coordinator', { defaultValue: 'Coordinator' })}
                            placeholder={t('coordinator', { defaultValue: 'Coordinator' })}
                            value={selectedCoordinators}
                            onChange={(val) => setSelectedCoordinators(val)}
                            data={coordinatorOptions}
                            radius="md"
                            size="sm"
                        />
                        <MultiSelect
                            className='w-[100%]'
                            clearable
                            searchable
                            label={t('reservation_status')}
                            placeholder={t('reservation_status')}
                            value={selectedStatuses}
                            onChange={(val) => setSelectedStatuses(val)}
                            data={RESERVATION_STATUS_ALL(t)}
                            renderOption={({ option }) => {
                                const statusConfig = RESERVATION_STATUS[option.value as keyof typeof RESERVATION_STATUS];
                                return (
                                    <div className="flex items-center gap-2">
                                        {statusConfig && (
                                            <span
                                                className="w-2 h-2 rounded-full shrink-0"
                                                style={{ backgroundColor: statusConfig.color }}
                                            />
                                        )}
                                        <span>{option.label}</span>
                                    </div>
                                );
                            }}
                            radius="md"
                            size="sm"
                        />
                        <MultiSelect
                            className='w-[100%]'
                            clearable
                            searchable
                            label={t('payment_status')}
                            placeholder={t('payment_status')}
                            value={selectedPaymentStatuses}
                            onChange={(val) => setSelectedPaymentStatuses(val)}
                            data={paymentStatusOptions}
                            radius="md"
                            size="sm"
                        />
                        <MultiSelect
                            className='w-[100%]'
                            clearable
                            searchable
                            label={t('service') || 'Service ID'}
                            placeholder={t('service') || 'Service ID'}
                            value={selectedServiceIds}
                            onChange={(val) => setSelectedServiceIds(val)}
                            data={serviceOptions}
                            radius="md"
                            size="sm"
                        />
                    </div>

                    {bookingAttributeFilters.size > 0 && (
                        <div className="space-y-2 border-t border-slate-100 pt-3">
                            <div className="font-bold text-xs text-slate-500 uppercase tracking-wider">
                                {t('booking_attributes')}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {Array.from(bookingAttributeFilters.entries()).map(([key, options]) => (
                                    <MultiSelect
                                        key={key}
                                        clearable
                                        searchable
                                        label={t(key)}
                                        placeholder={t(key)}
                                        value={selectedBookingAttrs[key] || []}
                                        onChange={(val) => {
                                            setSelectedBookingAttrs((prev) => ({
                                                ...prev,
                                                [key]: val
                                            }));
                                        }}
                                        data={options.map(opt => ({ value: opt, label: t(opt) }))}
                                        radius="md"
                                        size="sm"
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {serviceAttributeFilters.size > 0 && (
                        <div className="space-y-2 border-t border-slate-100 pt-3">
                            <div className="font-bold text-xs text-slate-500 uppercase tracking-wider">
                                {t('service_attributes')}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {Array.from(serviceAttributeFilters.entries()).map(([key, options]) => (
                                    <MultiSelect
                                        key={key}
                                        clearable
                                        searchable
                                        label={t(key)}
                                        placeholder={t(key)}
                                        value={selectedServiceAttrs[key] || []}
                                        onChange={(val) => {
                                            setSelectedServiceAttrs((prev) => ({
                                                ...prev,
                                                [key]: val
                                            }));
                                        }}
                                        data={options.map(opt => ({ value: opt, label: t(opt) }))}
                                        radius="md"
                                        size="sm"
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </CardModal>

            {/* Display Settings Modal */}
            <CardModal
                size="full"
                opened={showDisplayModal}
                onClose={() => setShowDisplayModal(false)}
                header={<div className="font-bold text-lg">{t('display_settings')}</div>}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="subtle"
                            color="gray"
                            onClick={() => {
                                setDisplayOptions(DEFAULT_OPTIONS);
                            }}
                        >
                            {t('reset_defaults')}
                        </Button>
                        <Button
                            color="black"
                            onClick={() => setShowDisplayModal(false)}
                        >
                            {t('close')}
                        </Button>
                    </div>
                }
            >
                <div className="space-y-4">
                    {/* View Columns Groups */}
                    <div className="space-y-2">
                        <div className="font-bold text-xs text-slate-500 uppercase tracking-wider">
                            {t('visible_columns')}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            {/* Group 1: Service Info */}
                            <div className="bg-slate-50/50 border border-slate-100 rounded-lg p-2.5 flex flex-col gap-2">
                                <div className="font-bold text-[10px] text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-1 mb-0.5">
                                    {t('general_service_info')}
                                </div>
                                <Switch
                                    checked={displayOptions.showServiceTitle}
                                    label={t('title_big_or_small')}
                                    onChange={() => handleToggleOption('showServiceTitle')}
                                    size="sm"
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.hideServiceTitle}
                                    label={t('hide_service_title')}
                                    onChange={() => handleToggleOption('hideServiceTitle')}
                                    size="sm"
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.showServiceOrder}
                                    label={t('service_order')}
                                    onChange={() => handleToggleOption('showServiceOrder')}
                                    size="sm"
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.showProvider}
                                    label={t('provider')}
                                    onChange={() => handleToggleOption('showProvider')}
                                    size="sm"
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.showCoordinator}
                                    label={t('coordinator')}
                                    onChange={() => handleToggleOption('showCoordinator')}
                                    size="sm"
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.showHour}
                                    label={t('hour')}
                                    onChange={() => handleToggleOption('showHour')}
                                    size="sm"
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.hideServicePackage}
                                    label={t('hide_package_parents')}
                                    onChange={() => handleToggleOption('hideServicePackage')}
                                    size="sm"
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.showExternalCode}
                                    label={t('external_code')}
                                    onChange={() => handleToggleOption('showExternalCode')}
                                    size="sm"
                                    color="indigo"
                                />
                            </div>

                            {/* Group 2: Reservation & Client */}
                            <div className="bg-slate-50/50 border border-slate-100 rounded-lg p-2.5 flex flex-col gap-2">
                                <div className="font-bold text-[10px] text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-1 mb-0.5">
                                    {t('reservation_client')}
                                </div>
                                <Switch
                                    checked={displayOptions.showReservation}
                                    label={t('reservation')}
                                    onChange={() => handleToggleOption('showReservation')}
                                    size="sm"
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.showBookingId}
                                    label={t('booking_id')}
                                    onChange={() => handleToggleOption('showBookingId')}
                                    size="sm"
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.showClientName}
                                    label={t('client_name')}
                                    onChange={() => handleToggleOption('showClientName')}
                                    size="sm"
                                    color="indigo"
                                />
                            </div>

                            {/* Group 3: Passengers */}
                            <div className="bg-slate-50/50 border border-slate-100 rounded-lg p-2.5 flex flex-col gap-2">
                                <div className="font-bold text-[10px] text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-1 mb-0.5">
                                    {t('paxs')}
                                </div>
                                <Switch
                                    checked={displayOptions.showPaxs}
                                    label={t('paxs')}
                                    onChange={() => handleToggleOption('showPaxs')}
                                    size="sm"
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.showPaxName}
                                    label={t('pax_name')}
                                    onChange={() => handleToggleOption('showPaxName')}
                                    size="sm"
                                    disabled={!displayOptions.showPaxs}
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.showPaxPhone}
                                    label={t('pax_phone')}
                                    onChange={() => handleToggleOption('showPaxPhone')}
                                    size="sm"
                                    disabled={!displayOptions.showPaxs}
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.showPaxEmail}
                                    label={t('pax_email')}
                                    onChange={() => handleToggleOption('showPaxEmail')}
                                    size="sm"
                                    disabled={!displayOptions.showPaxs}
                                    color="indigo"
                                />
                            </div>

                            {/* Group 4: Finance */}
                            <div className="bg-slate-50/50 border border-slate-100 rounded-lg p-2.5 flex flex-col gap-2">
                                <div className="font-bold text-[10px] text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-1 mb-0.5">
                                    {t('finance')}
                                </div>
                                <Switch
                                    checked={displayOptions.showPrice}
                                    label={t('price')}
                                    onChange={() => handleToggleOption('showPrice')}
                                    size="sm"
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.showCost}
                                    label={t('cost')}
                                    onChange={() => handleToggleOption('showCost')}
                                    size="sm"
                                    color="indigo"
                                />
                                <Switch
                                    checked={displayOptions.showPaymentStatus}
                                    label={t('payment_status')}
                                    onChange={() => handleToggleOption('showPaymentStatus')}
                                    size="sm"
                                    color="indigo"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Attributes to show selection */}
                    {allAttrKeys.length > 0 && (
                        <div className="space-y-2 border-t border-slate-100 pt-3">
                            <div className="font-bold text-[10px] text-slate-500 uppercase tracking-wider mb-2">
                                {t('attributes_to_show')}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 bg-slate-50/50 border border-slate-100 rounded-lg p-2.5">
                                {allAttrKeys.map(key => {
                                    const isChecked = (displayOptions.selectedAttrKeys || []).includes(key);
                                    return (
                                        <Switch
                                            key={key}
                                            checked={isChecked}
                                            label={t(key)}
                                            onChange={() => {
                                                const current = displayOptions.selectedAttrKeys || [];
                                                const next = isChecked
                                                    ? current.filter(k => k !== key)
                                                    : [...current, key];
                                                setDisplayOptions((prev: any) => ({
                                                    ...prev,
                                                    selectedAttrKeys: next
                                                }));
                                            }}
                                            size="sm"
                                            color="indigo"
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </CardModal>

            {/* Save Preset Modal */}
            <SavePresetModal
                opened={showSaveModal}
                onClose={() => setShowSaveModal(false)}
                params={{
                    reservationDate,
                    startDateRange,
                    endDateRange,
                    bookingDate,
                    searchText,
                    selectedCategories,
                    selectedProviders,
                    selectedCoordinators,
                    selectedClients,
                    selectedStatuses,
                    selectedPaymentStatuses,
                    selectedServiceIds,
                    selectedBookingAttrs,
                    selectedServiceAttrs,
                }}
                onSaveSuccess={(presetId) => setSelectedPresetId(presetId)}
            />

            {isLoading && <SkeletonList />}

        </div>
    );
}
